import { createContext } from 'react';

import { getRoundState } from '../components/_organisms/LiveGame';
import { Slide } from '../components/SlideComponents/slide.types';
import { DEFAULT_AUDIO_VOLUME } from '../hooks/useAudio';
import { Game } from '../services/backendService/types';
import { getRoundCategory } from './roundUtils';

export type MusicState = {
  song?: string;
  isLooping: boolean;
  autoplay: boolean;
  volume?: number;
};

export const GameMusicContext = createContext<{
  music: MusicState;
  setMusic: React.Dispatch<React.SetStateAction<MusicState>>;
}>({
  music: {
    isLooping: true,
    autoplay: true,
    song: undefined,
    volume: DEFAULT_AUDIO_VOLUME,
  },
  setMusic: () => undefined,
});
export const musicMapper = {
  lobby: {
    song: '/sum_of_funk.wav',
    loop: true,
  },
  ask: {
    song: '/questions_blazer_rail.wav',
    loop: true,
  },
  idle: {
    song: '/end_of_questions.wav',
    loop: false,
  },
  vote: {
    song: '/voting_waiting_game.wav',
    loop: true,
  },
  leaderboard: {
    song: '/leaderboard.wav',
    loop: false,
  },
};

export const SOUND_PROFILE_OPTIONS = [
  { value: 'default', label: 'Default' },
  { value: 'jungle', label: 'Jungle' },
  { value: 'winterWonder', label: 'Winter Wonder' },
  { value: 'mountainVibe', label: 'Mountain Vibe' },
  { value: 'outerSpace', label: 'Outer Space' },
  { value: 'wizardWorld', label: 'Wizard World' },
  { value: 'funky', label: 'Funky' },
] as const;

export type SoundProfile = (typeof SOUND_PROFILE_OPTIONS)[number]['value'];

type SongConfig = {
  song?: string;
  loop: boolean;
};

type SoundProfileConfig = {
  lobby: SongConfig;
  ask: SongConfig;
  idle: SongConfig;
  vote: SongConfig;
  leaderboard: SongConfig;
};

const sharedSilentStateSong: SongConfig = {
  song: undefined,
  loop: false,
};

const soundProfileMapper: Record<SoundProfile, SoundProfileConfig> = {
  default: {
    lobby: { song: '/classic_lobby.mp3', loop: true },
    ask: { song: '/classic_ask.mp3', loop: true },
    idle: { song: '/end_of_questions.wav', loop: false },
    vote: { song: '/classic_vote.mp3', loop: true },
    leaderboard: { song: '/leaderboard.wav', loop: false },
  },
  jungle: {
    lobby: { song: '/themes/animals/lobby.wav', loop: true },
    ask: { song: '/themes/animals/questions.wav', loop: true },
    idle: sharedSilentStateSong,
    vote: { song: '/themes/animals/questions.wav', loop: true },
    leaderboard: sharedSilentStateSong,
  },
  winterWonder: {
    lobby: { song: '/themes/christmas/lobby.wav', loop: true },
    ask: { song: '/themes/christmas/questions.wav', loop: true },
    idle: sharedSilentStateSong,
    vote: { song: '/themes/christmas/questions.wav', loop: true },
    leaderboard: sharedSilentStateSong,
  },
  mountainVibe: {
    lobby: { song: '/themes/himalayas/lobby.wav', loop: true },
    ask: { song: '/themes/himalayas/questions.wav', loop: true },
    idle: sharedSilentStateSong,
    vote: { song: '/themes/himalayas/questions.wav', loop: true },
    leaderboard: sharedSilentStateSong,
  },
  outerSpace: {
    lobby: { song: '/themes/space/lobby.wav', loop: true },
    ask: { song: '/themes/space/questions.wav', loop: true },
    idle: sharedSilentStateSong,
    vote: { song: '/themes/space/questions.wav', loop: true },
    leaderboard: sharedSilentStateSong,
  },
  wizardWorld: {
    lobby: { song: '/themes/wizard/lobby.wav', loop: true },
    ask: { song: '/themes/wizard/questions.wav', loop: true },
    idle: sharedSilentStateSong,
    vote: { song: '/themes/wizard/questions.wav', loop: true },
    leaderboard: sharedSilentStateSong,
  },
  funky: {
    lobby: { song: '/themes/curiQuiz/lobby.wav', loop: true },
    ask: { song: '/themes/curiQuiz/questions.wav', loop: true },
    idle: sharedSilentStateSong,
    vote: { song: '/themes/curiQuiz/questions.wav', loop: true },
    leaderboard: sharedSilentStateSong,
  },
};

// Sound configuration type for themed blocks
type BlockSoundConfig = {
  lobby: { song: string; loop: boolean };
  ask: { song: string; loop: boolean };
};

// Maps block names to their sound configurations
// Only blocks with custom sounds need to be listed here
const blockSoundMapper: Record<string, BlockSoundConfig> = {
  themedPracticeAnimalsActivity: {
    lobby: { song: '/themes/animals/lobby.wav', loop: true },
    ask: { song: '/themes/animals/questions.wav', loop: true },
  },
  themedPracticeWizardActivity: {
    lobby: { song: '/themes/wizard/lobby.wav', loop: true },
    ask: { song: '/themes/wizard/questions.wav', loop: true },
  },
  themedPracticeChristmasActivity: {
    lobby: { song: '/themes/christmas/lobby.wav', loop: true },
    ask: { song: '/themes/christmas/questions.wav', loop: true },
  },
  themedPracticeSpaceActivity: {
    lobby: { song: '/themes/space/lobby.wav', loop: true },
    ask: { song: '/themes/space/questions.wav', loop: true },
  },
  themedPracticeHimalayasActivity: {
    lobby: { song: '/themes/himalayas/lobby.wav', loop: true },
    ask: { song: '/themes/himalayas/questions.wav', loop: true },
  },
  curiQuizActivity: {
    lobby: { song: '/themes/curiQuiz/lobby.wav', loop: true },
    ask: { song: '/themes/curiQuiz/questions.wav', loop: true },
  },
  // Add more themed blocks as they are implemented:
  // themedPracticeChristmasActivity: {
  //   lobby: { song: '/themes/christmas/lobby.wav', loop: true },
  //   ask: { song: '/themes/christmas/questions.wav', loop: true },
  // },
};

// Get sound config for a game based on its slides' block labels
const getBlockSoundConfig = (
  slides: Slide[] | null | undefined,
): BlockSoundConfig | undefined => {
  if (!slides) return undefined;

  for (const slide of slides) {
    if (slide.blockLabel && blockSoundMapper[slide.blockLabel]) {
      return blockSoundMapper[slide.blockLabel];
    }
  }
  return undefined;
};

const isSoundProfile = (value?: string): value is SoundProfile =>
  SOUND_PROFILE_OPTIONS.some(option => option.value === value);

const getSoundProfileFromSettings = (
  settings: Game['settings'] | undefined,
): SoundProfile | undefined => {
  const soundProfileValue = settings?.soundProfile;

  if (typeof soundProfileValue !== 'string') {
    return undefined;
  }

  return isSoundProfile(soundProfileValue) ? soundProfileValue : undefined;
};

const inferLegacySoundProfile = (slides: Slide[] | null | undefined): SoundProfile => {
  if (!slides) return 'default';

  for (const slide of slides) {
    switch (slide.blockLabel) {
      case 'themedPracticeAnimalsActivity':
        return 'jungle';
      case 'themedPracticeChristmasActivity':
        return 'winterWonder';
      case 'themedPracticeHimalayasActivity':
        return 'mountainVibe';
      case 'themedPracticeSpaceActivity':
        return 'outerSpace';
      case 'themedPracticeWizardActivity':
        return 'wizardWorld';
      case 'curiQuizActivity':
        return 'funky';
      default:
    }
  }

  return 'default';
};

export const getResolvedSoundProfile = (game: Game): SoundProfile =>
  getSoundProfileFromSettings(game.settings) || inferLegacySoundProfile(game.slides);

export const findRoundSong = (
  game: Game,
  now: () => number,
  showLobby: boolean,
  enableMusicToggle = false,
) => {
  const slideState = getRoundState({ game, now });

  const slide = game.slides?.find(slide => slide.id === game.currentRound);

  const selectedSoundProfile = enableMusicToggle
    ? getSoundProfileFromSettings(game.settings)
    : undefined;
  const selectedSoundConfig = selectedSoundProfile
    ? soundProfileMapper[selectedSoundProfile]
    : undefined;

  // Check if this game uses custom block sounds (e.g., themed practice)
  const blockSounds = selectedSoundConfig ? undefined : getBlockSoundConfig(game.slides);
  const hasCustomSounds = !!blockSounds;

  // Lobby music
  if (showLobby && slideState !== 'in' && slideState !== 'countdown') {
    if (selectedSoundConfig) return selectedSoundConfig.lobby;
    return blockSounds?.lobby || musicMapper.lobby;
  }

  if (!showLobby && slideState === 'pre') return null;
  // For blocks with custom sounds, don't play idle/transition music
  if (!showLobby && slideState === 'post-between') {
    if (selectedSoundConfig) return selectedSoundConfig.idle.song ? selectedSoundConfig.idle : null;
    return hasCustomSounds ? null : musicMapper.idle;
  }

  const isInSlideCountdown =
    game.isPlaying &&
    slideState === 'in' &&
    game.roundStartsAt &&
    slide?.durationSeconds &&
    new Date(game.roundStartsAt + slide?.durationSeconds * 1000).getTime() > now();

  const slideCategory = getRoundCategory(slide?.type);

  // Question/response phase
  if (isInSlideCountdown && slideCategory === 'response') {
    if (selectedSoundConfig) return selectedSoundConfig.ask;
    return blockSounds?.ask || musicMapper.ask;
  }

  const isInPreRoundCountdown =
    game.isPlaying &&
    slideState === 'countdown' &&
    game.roundStartsAt &&
    game.roundStartsAt > now();

  if (isInPreRoundCountdown && slideCategory === 'response') {
    if (selectedSoundConfig) return selectedSoundConfig.ask;
    return blockSounds?.ask || musicMapper.ask;
  }

  // For blocks with custom sounds, don't play vote music
  if (isInPreRoundCountdown && slideCategory === 'collaboration') {
    if (selectedSoundConfig) return selectedSoundConfig.vote.song ? selectedSoundConfig.vote : null;
    return hasCustomSounds ? null : musicMapper.vote;
  }

  // For blocks with custom sounds, don't play leaderboard music
  if (game.isPlaying && slideCategory === 'results' && !slide?.wasPlayed) {
    if (selectedSoundConfig)
      return selectedSoundConfig.leaderboard.song ? selectedSoundConfig.leaderboard : null;
    return hasCustomSounds ? null : musicMapper.leaderboard;
  }

  if (game.isPlaying && slideState === 'pre') {
    // prevent playing music when moving from lobby straight to activity
    return null;
  }
  if (game.isPlaying && slideCategory === 'results' && slide?.wasPlayed) {
    // prevent playing music when the round has been played
    return null;
  }

  if (game.isPlaying && slideState === 'post' && !game.roundStartsAt) {
    // prevent playing music when navigating between slides
    return null;
  }
  // For blocks with custom sounds, don't play idle music on post state
  if (game.isPlaying && slideState === 'post') {
    if (selectedSoundConfig) return selectedSoundConfig.idle.song ? selectedSoundConfig.idle : null;
    return hasCustomSounds ? null : musicMapper.idle;
  }

  // keep playing
  return undefined;
};

export const calculateVolume = (vol: number) => {
  const cubicVolume = Math.pow(vol, 3);

  return cubicVolume;
};
